// Football (FIFA rules) match result dialog, shared by the group cell and
// bracket node editors. A football match is a single goals score — plus, for a
// drawn knock-out match, a penalty shoot-out — with an explicit "final result"
// action: a drawn 1–1 can be a live score (streamed to spectators) or the
// final result, and only the organiser can tell the difference (see
// `features/football-tournaments.md`).
//
// Below the score the dialog tracks the referee's report: who scored each goal
// (with own goals) and which squad members were shown yellow/red cards. The
// event list is saved with the score in the same round-trip and validated
// against it — a side can never have more attributed scorers than goals.
//
// Like CellEditor/BracketNode the dialog stays controlled: the draft is local
// input state only, every save round-trips through the server cascade.

import { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import type {
  FootballCardEvent,
  FootballGoalEvent,
  FootballMatchEvent,
  FootballPhase,
  GameScore,
  Referee,
} from '../domain';
import {
  FOOTBALL_MINUTE_MAX,
  isFootballResultComplete,
  validateFootballEvents,
  validateFootballGames,
} from '../domain';
import { useTournament } from '../state/TournamentContext';
import { useTranslation } from '../state/I18nContext';

const toGoals = (value: string) => Math.max(0, Math.trunc(Number(value)) || 0);

/** Parse a minute input: empty = unrecorded, else clamped into 1..max. */
const toMinute = (value: string): number | null => {
  if (value.trim() === '') return null;
  const minute = Math.trunc(Number(value)) || 1;
  return Math.min(FOOTBALL_MINUTE_MAX, Math.max(1, minute));
};

interface FootballMatchDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle: string;
  /** Cell_… or Node_… id, used for the referee assignment. */
  matchId: string;
  games: readonly GameScore[];
  /** The match's stored goal/card events (entry order). */
  events: readonly FootballMatchEvent[];
  phase: FootballPhase;
  refereeId: string | null;
  nameA: string;
  nameB: string;
  /** Squad member names of each team, in list order (may be empty). */
  membersA: readonly string[];
  membersB: readonly string[];
}

export function FootballMatchDialog({
  open,
  onClose,
  title,
  subtitle,
  matchId,
  games,
  events,
  phase,
  refereeId,
  nameA,
  nameB,
  membersA,
  membersB,
}: FootballMatchDialogProps) {
  const { tournament, updateCell, updateNode, assignReferee } = useTournament();
  const { t } = useTranslation();

  const [goalsA, setGoalsA] = useState(0);
  const [goalsB, setGoalsB] = useState(0);
  const [pensA, setPensA] = useState(0);
  const [pensB, setPensB] = useState(0);
  const [draftEvents, setDraftEvents] = useState<FootballMatchEvent[]>([]);

  // Reset the draft from the persisted score when the dialog (re)opens or the
  // stored score itself changes (e.g. another tab saved). Keyed on the score's
  // CONTENT, not the array identity: every snapshot round-trip (assigning a
  // referee from this very dialog, an SSE refresh) rebuilds the array, and an
  // identity-keyed reset would wipe the operator's unsaved goal entries.
  const gamesKey = JSON.stringify(games);
  const eventsKey = JSON.stringify(events);
  useEffect(() => {
    if (!open) return;
    const stored = JSON.parse(gamesKey) as GameScore[];
    setGoalsA(stored[0]?.pointsA ?? 0);
    setGoalsB(stored[0]?.pointsB ?? 0);
    setPensA(stored[1]?.pointsA ?? 0);
    setPensB(stored[1]?.pointsB ?? 0);
    setDraftEvents(JSON.parse(eventsKey) as FootballMatchEvent[]);
  }, [open, gamesKey, eventsKey]);

  const referees = useMemo<Referee[]>(
    () =>
      tournament
        ? Object.values(tournament.referees).sort((a, b) => a.seed - b.seed)
        : [],
    [tournament],
  );

  const drawn = goalsA === goalsB;
  const showShootout = phase === 'bracket' && drawn;
  // The shoot-out entry only exists once someone recorded a conversion — a
  // drawn match with an untouched 0–0 shoot-out is simply "not decided yet".
  const draft: GameScore[] =
    showShootout && (pensA > 0 || pensB > 0)
      ? [
          { pointsA: goalsA, pointsB: goalsB },
          { pointsA: pensA, pointsB: pensB },
        ]
      : [{ pointsA: goalsA, pointsB: goalsB }];

  const validation = validateFootballGames(draft, phase);
  const eventsValidation = validateFootballEvents(
    draftEvents,
    draft,
    membersA.length,
    membersB.length,
  );
  const saveable = validation.valid && eventsValidation.valid;
  const finalizable = saveable && isFootballResultComplete(draft, phase);

  const rosterOf = (side: 'A' | 'B') => (side === 'A' ? membersA : membersB);
  /** The roster a goal's scorer is picked from (the opponent's for an own goal). */
  const scorerRoster = (goal: FootballGoalEvent) =>
    rosterOf(goal.ownGoal ? (goal.side === 'A' ? 'B' : 'A') : goal.side);
  const noMembers = membersA.length === 0 && membersB.length === 0;

  const replaceEvent = (index: number, next: FootballMatchEvent) =>
    setDraftEvents((list) => list.map((e, i) => (i === index ? next : e)));
  const removeEvent = (index: number) =>
    setDraftEvents((list) => list.filter((_, i) => i !== index));
  const addGoal = () =>
    setDraftEvents((list) => [
      ...list,
      {
        kind: 'GOAL',
        side: membersA.length > 0 ? 'A' : 'B',
        memberIndex: 0,
        ownGoal: false,
        minute: null,
      },
    ]);
  const addCard = () =>
    setDraftEvents((list) => [
      ...list,
      {
        kind: 'CARD',
        side: membersA.length > 0 ? 'A' : 'B',
        memberIndex: 0,
        card: 'YELLOW',
        minute: null,
      },
    ]);

  const update = (
    matchGames: GameScore[],
    final: boolean,
    matchEvents: FootballMatchEvent[],
  ) =>
    matchId.startsWith('Cell_')
      ? updateCell(matchId, matchGames, final, matchEvents)
      : updateNode(matchId, matchGames, final, matchEvents);

  const saveLive = () => {
    if (!saveable) return;
    void update(draft, false, draftEvents);
    onClose();
  };

  const saveFinal = () => {
    if (!finalizable) return;
    void update(draft, true, draftEvents);
    onClose();
  };

  const clear = () => {
    void update([], false, []);
    onClose();
  };

  /** The team + squad-member + minute controls shared by goal and card rows. */
  const memberPickers = (
    event: FootballGoalEvent | FootballCardEvent,
    index: number,
    roster: readonly string[],
    memberLabel: string,
    onSideChange: (side: 'A' | 'B') => void,
  ) => (
    <>
      <TextField
        select
        size="small"
        label={t('admin.football.events.teamLabel')}
        value={event.side}
        onChange={(e) => onSideChange(e.target.value === 'A' ? 'A' : 'B')}
        sx={{ width: 130 }}
      >
        <MenuItem value="A">{nameA}</MenuItem>
        <MenuItem value="B">{nameB}</MenuItem>
      </TextField>
      <TextField
        select
        size="small"
        label={memberLabel}
        value={roster.length > 0 ? Math.min(event.memberIndex, roster.length - 1) : ''}
        onChange={(e) =>
          replaceEvent(index, { ...event, memberIndex: Number(e.target.value) })
        }
        disabled={roster.length === 0}
        sx={{ flex: 1, minWidth: 150 }}
      >
        {roster.map((member, memberIndex) => (
          <MenuItem key={memberIndex} value={memberIndex}>
            {memberIndex + 1}. {member}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        type="number"
        size="small"
        label={t('admin.football.events.minuteLabel')}
        value={event.minute ?? ''}
        onChange={(e) =>
          replaceEvent(index, { ...event, minute: toMinute(e.target.value) })
        }
        sx={{ width: 74 }}
        slotProps={{ htmlInput: { min: 1, max: FOOTBALL_MINUTE_MAX } }}
      />
    </>
  );

  const goalRow = (goal: FootballGoalEvent, index: number) => (
    <Box
      key={index}
      sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
    >
      {memberPickers(
        goal,
        index,
        scorerRoster(goal),
        t('admin.football.events.scorerLabel'),
        (side) => replaceEvent(index, { ...goal, side, memberIndex: 0 }),
      )}
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={goal.ownGoal}
            onChange={(e) =>
              replaceEvent(index, {
                ...goal,
                ownGoal: e.target.checked,
                memberIndex: 0,
              })
            }
          />
        }
        label={
          <Typography variant="caption">
            {t('admin.football.events.ownGoal')}
          </Typography>
        }
        sx={{ mr: 0 }}
      />
      <IconButton
        size="small"
        aria-label={t('admin.football.events.removeAria')}
        onClick={() => removeEvent(index)}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  const cardRow = (card: FootballCardEvent, index: number) => (
    <Box
      key={index}
      sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
    >
      {memberPickers(
        card,
        index,
        rosterOf(card.side),
        t('admin.football.events.playerLabel'),
        (side) => replaceEvent(index, { ...card, side, memberIndex: 0 }),
      )}
      <TextField
        select
        size="small"
        label={t('admin.football.events.cardLabel')}
        value={card.card}
        onChange={(e) =>
          replaceEvent(index, {
            ...card,
            card: e.target.value === 'RED' ? 'RED' : 'YELLOW',
          })
        }
        sx={{ width: 110 }}
      >
        <MenuItem value="YELLOW">{t('admin.football.events.yellow')}</MenuItem>
        <MenuItem value="RED">{t('admin.football.events.red')}</MenuItem>
      </TextField>
      <IconButton
        size="small"
        aria-label={t('admin.football.events.removeAria')}
        onClick={() => removeEvent(index)}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  const eventSection = (kind: 'GOAL' | 'CARD') => (
    <Box>
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography variant="subtitle2">
          {kind === 'GOAL'
            ? t('admin.football.events.goalsTitle')
            : t('admin.football.events.cardsTitle')}
        </Typography>
        <Button
          size="small"
          startIcon={<AddIcon fontSize="small" />}
          onClick={kind === 'GOAL' ? addGoal : addCard}
          disabled={noMembers}
        >
          {kind === 'GOAL'
            ? t('admin.football.events.addGoal')
            : t('admin.football.events.addCard')}
        </Button>
      </Box>
      <Stack spacing={1} sx={{ mt: 0.5 }}>
        {draftEvents.map((event, index) =>
          event.kind === kind
            ? event.kind === 'GOAL'
              ? goalRow(event, index)
              : cardRow(event, index)
            : null,
        )}
      </Stack>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>

        <Stack spacing={2} sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              type="number"
              size="small"
              label={nameA}
              aria-label={t('admin.football.goalsFor', { name: nameA })}
              value={goalsA}
              onChange={(e) => setGoalsA(toGoals(e.target.value))}
              sx={{ flex: 1 }}
              slotProps={{ htmlInput: { min: 0 } }}
            />
            <Typography color="text.secondary">–</Typography>
            <TextField
              type="number"
              size="small"
              label={nameB}
              aria-label={t('admin.football.goalsFor', { name: nameB })}
              value={goalsB}
              onChange={(e) => setGoalsB(toGoals(e.target.value))}
              sx={{ flex: 1 }}
              slotProps={{ htmlInput: { min: 0 } }}
            />
          </Box>

          {showShootout && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('admin.football.shootoutHint')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <TextField
                  type="number"
                  size="small"
                  label={t('admin.football.shootoutLabel')}
                  aria-label={t('admin.football.shootoutFor', { name: nameA })}
                  value={pensA}
                  onChange={(e) => setPensA(toGoals(e.target.value))}
                  sx={{ flex: 1 }}
                  slotProps={{ htmlInput: { min: 0 } }}
                />
                <Typography color="text.secondary">–</Typography>
                <TextField
                  type="number"
                  size="small"
                  label={t('admin.football.shootoutLabel')}
                  aria-label={t('admin.football.shootoutFor', { name: nameB })}
                  value={pensB}
                  onChange={(e) => setPensB(toGoals(e.target.value))}
                  sx={{ flex: 1 }}
                  slotProps={{ htmlInput: { min: 0 } }}
                />
              </Box>
            </Box>
          )}

          {eventSection('GOAL')}
          {eventSection('CARD')}
          {noMembers && (
            <Typography variant="caption" color="text.secondary">
              {t('admin.football.events.noMembersHint')}
            </Typography>
          )}

          {referees.length > 0 && (
            <TextField
              select
              size="small"
              fullWidth
              label={t('admin.football.refereeLabel')}
              value={refereeId ?? ''}
              onChange={(e) =>
                void assignReferee(
                  matchId,
                  e.target.value === '' ? null : e.target.value,
                )
              }
            >
              <MenuItem value="">{t('admin.football.refereeNone')}</MenuItem>
              {referees.map((referee) => (
                <MenuItem key={referee.id} value={referee.id}>
                  {referee.name}
                </MenuItem>
              ))}
            </TextField>
          )}

          {!validation.valid && validation.code && (
            <Typography variant="caption" color="warning.main">
              {t(`admin.football.invalid.${validation.code}`)}
            </Typography>
          )}
          {validation.valid && !eventsValidation.valid && eventsValidation.code && (
            <Typography variant="caption" color="warning.main">
              {t(`admin.football.events.invalid.${eventsValidation.code}`, {
                max: FOOTBALL_MINUTE_MAX,
              })}
            </Typography>
          )}
          {saveable && !finalizable && phase === 'bracket' && drawn && (
            <Typography variant="caption" color="text.secondary">
              {t('admin.football.needWinnerHint')}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 0.5 }}>
        <Button color="inherit" onClick={clear}>
          {t('admin.football.clear')}
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose}>{t('admin.common.cancel')}</Button>
        <Button disabled={!saveable} onClick={saveLive}>
          {t('admin.football.saveLive')}
        </Button>
        <Button variant="contained" disabled={!finalizable} onClick={saveFinal}>
          {t('admin.football.saveFinal')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
